"use client";

import React from "react";
import dynamic from "next/dynamic";
import FormLabel from "@mui/material/FormLabel";
import { QuillOptionsStatic } from "react-quill-new";

import { LIMITS } from "@/utils/constants";
import { registerMaxLengthModule } from "./registerMaxLength";

const ReactQuill = dynamic(
  async () => {
    const reactQuillModule = await import("react-quill-new");
    registerMaxLengthModule(reactQuillModule.Quill);
    return reactQuillModule.default;
  },
  { ssr: false }
);

interface EditorProps extends QuillOptionsStatic {
  value?: string;
  onChange?: (value: string) => void;
  readOnly?: boolean;
  errorText?: any;
  label?: string;
  defaultValue?: string;
  placeholder?: string;
}

const maxLength = LIMITS.description;

const TextEditorComponent: React.FC<EditorProps> = ({
  value,
  onChange,
  readOnly,
  errorText,
  label,
  defaultValue,
  placeholder,
}) => {
  const editorRootRef = React.useRef<HTMLDivElement>(null);
  const generatedId = React.useId().replace(/:/g, "");

  React.useEffect(() => {
    const identifyTooltipInput = () => {
      const tooltipInput =
        editorRootRef.current?.querySelector<HTMLInputElement>(
          ".ql-tooltip input"
        );

      if (!tooltipInput) return;

      const tooltipInputId = `quill-link-${generatedId}`;
      tooltipInput.id ||= tooltipInputId;
      tooltipInput.name ||= tooltipInputId;
    };

    identifyTooltipInput();

    const observer = new MutationObserver(identifyTooltipInput);
    if (editorRootRef.current) {
      observer.observe(editorRootRef.current, {
        childList: true,
        subtree: true,
      });
    }

    return () => observer.disconnect();
  }, [generatedId]);

  return (
    <div ref={editorRootRef}>
      {label && (
        <FormLabel
          sx={{
            color: "#1F4970",
            fontSize: "14px",
            fontWeight: "600",
            padding: "12px 4px",
            marginTop: "10px",
          }}
        >
          {label}
        </FormLabel>
      )}
      <ReactQuill
        modules={{
          toolbar: [
            ["bold", "italic", "underline", "strike"],
            [{ list: "ordered" }, { list: "bullet" }],
            ["link"],
            ["clean"],
          ],
          maxlength: { maxLength }, // Apply maxLength module
        }}
        readOnly={!!readOnly}
        value={value}
        defaultValue={defaultValue}
        placeholder={placeholder}
        preserveWhitespace={true}
        onChange={(val) => {
          // Allow typing even when over limit - validation is handled via errorText prop
          onChange?.(val);
        }}
        className={readOnly ? "quill-readonly" : ""}
        style={{
          border: `1px solid ${errorText ? "red" : "#f3f3f3"}`,
          marginTop: "10px",
          fontWeight: readOnly ? "600" : "400",
          color: readOnly ? "#a9a9a9" : "inherit",
        }}
      />
      <div style={{ fontSize: "12px", color: "red", marginLeft: "15px" }}>
        {errorText}
      </div>
    </div>
  );
};

export default React.memo(TextEditorComponent);
